/**
 * SDL Settings Screen
 *
 * Four tabs: Audio, Graphics, Account, Support.
 *
 * - Audio + Graphics write directly to the SettingsStore singleton, which
 *   persists to localStorage and emits `change`. `main.ts` subscribes and
 *   applies the values to the live AudioManager / SharedRenderer — this screen
 *   never touches the engine itself.
 * - Account + Support actions emit events (`account:*`, `support:*`) that
 *   `main.ts` wires to auth/CDN/server flows. Picture upload (Bunny CDN),
 *   email change, and ticket submission need backend work tracked separately;
 *   the UI + events are ready for them.
 *
 * Scoped styles live in styles/settings.css under `.settings-screen`.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import { settingsStore } from '../../settings/SettingsStore';
import { serverConfig } from '../../network/ServerConfig';
import { notificationService } from '../../services/NotificationService';
import type { RenderQuality } from '../../engine/render/SharedRenderer';
import type { BlockedPlayer } from '@sdl/shared';

/** Legal pages are served by main-server (see its /privacy and /terms routes),
 *  so build the URLs from the configured server origin — works in dev + prod. */
const LEGAL_BASE = serverConfig.mainServerUrl.replace(/\/$/, '');

export type SettingsTab = 'audio' | 'graphics' | 'account' | 'support';

export interface SettingsAccount {
  username: string;
  email: string;
  /** Avatar URL. Empty string falls back to initials. */
  picture: string;
  /** Guest accounts can't change email and should be nudged to register. */
  isGuest: boolean;
}

export interface SettingsScreenEvents {
  'account:change-picture': void;
  'account:update-email': void;
  'account:recover': void;
  /** Guest tapped a provider in the "Connect & bag 100M" section. */
  'account:connect': { provider: 'google' | 'apple' };
  'account:logout': void;
  'account:unblock': { playerId: string };
  'support:open-ticket': { subject: string; message: string };
  'support:feedback': { message: string };
  /** Player tapped "Rate us". main.ts opens the store listing. */
  'support:rate': void;
  /** Player flipped the push-notifications switch. main.ts drives the OS
   *  permission prompt / token (un)registration from this. */
  'notifications:toggle': { enabled: boolean };
  [key: string]: unknown;
}

const QUALITY_INFO: Record<RenderQuality, { label: string; blurb: string }> = {
  low: { label: 'LOW', blurb: 'Best battery & frame rate. For older phones.' },
  medium: { label: 'MEDIUM', blurb: 'Balanced visuals and performance.' },
  high: { label: 'HIGH', blurb: 'Full shadows & effects. Newer phones.' },
};

const APP_VERSION = (import.meta.env?.VITE_APP_VERSION as string) ?? '0.1.0-beta';

/** Connect-account bonus, formatted for display. Mirrors server
 *  config.game.connectBonus.stacks (100M). */
const CONNECT_BONUS_DISPLAY = '100,000,000';

// Provider button glyphs — shared with the login screen markup so the in-app
// "connect account" buttons read identically to the first-run sign-in.
const APPLE_GLYPH = `
  <svg width="18" height="22" viewBox="0 0 18 22" fill="none">
    <path d="M17.0703 17.1562C16.7422 17.9141 16.3555 18.6133 15.9082 19.2598C15.2949 20.1445 14.7949 20.7676 14.4141 21.1289C13.8281 21.6992 13.1992 21.9922 12.5234 22.0098C12.043 22.0098 11.4609 21.873 10.7832 21.5957C10.1035 21.3203 9.47656 21.1836 8.90039 21.1836C8.29688 21.1836 7.65234 21.3203 6.96484 21.5957C6.27734 21.873 5.71875 22.0176 5.28516 22.0332C4.63672 22.0625 3.99219 21.7617 3.34961 21.1289C2.93945 20.7402 2.41992 20.0977 1.79297 19.2012C1.12109 18.2441 0.570312 17.1328 0.140625 15.8652C-0.320312 14.4902 -0.554688 13.1582 -0.554688 11.8672C-0.554688 10.3945 -0.240234 9.12109 0.390625 8.05078C0.882812 7.19141 1.53516 6.53125 2.35156 6.06836C3.16797 5.60547 4.04883 5.36914 4.99805 5.35352C5.50781 5.35352 6.17578 5.51172 7.00586 5.82227C7.83398 6.13379 8.37305 6.29199 8.62109 6.29199C8.80859 6.29199 9.40625 6.10547 10.4004 5.73438C11.3379 5.39062 12.1309 5.24805 12.7832 5.30273C14.5527 5.44531 15.8789 6.14844 16.7559 7.41602C15.1758 8.37109 14.3945 9.72266 14.4102 11.4648C14.4258 12.8398 14.9238 13.9844 15.9004 14.8926C16.3379 15.3105 16.8262 15.6328 17.3711 15.8594C17.2773 16.1172 17.1777 16.3652 17.0703 16.6035V17.1562ZM12.8906 0.410156C12.8906 1.49609 12.4902 2.51172 11.6953 3.44922C10.7344 4.5625 9.57422 5.20312 8.31641 5.09961C8.30078 4.96875 8.29102 4.83008 8.29102 4.68359C8.29102 3.64258 8.74805 2.52734 9.55859 1.61523C9.96289 1.15234 10.4746 0.771484 11.0938 0.472656C11.7109 0.177734 12.2949 0.0136719 12.8438 -0.00195312C12.8594 0.138672 12.8906 0.275391 12.8906 0.410156Z" fill="black"/>
  </svg>`;

const GOOGLE_GLYPH = `
  <svg viewBox="0 0 24 24" width="18" height="18">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>`;

export class SettingsScreen extends EventEmitter<SettingsScreenEvents> {
  public element: HTMLElement;
  private activeTab: SettingsTab = 'account';
  /** Cached OS notification permission (granted?) — the toggle shows ON only
   *  when the OS permission is actually granted AND the player hasn't muted it.
   *  Refreshed asynchronously whenever the Account tab renders. */
  private notifGranted = false;
  private account: SettingsAccount = {
    username: 'Player',
    email: '',
    picture: '',
    isGuest: true,
  };
  private blocked: BlockedPlayer[] = [];
  private blockedExpanded = false;
  private connectExpanded = false;

  private tabBar!: HTMLElement;
  private panel!: HTMLElement;

  constructor() {
    super();
    this.element = this.createElement();
    this.renderPanel();
  }

  /** Push live account info (called from main.ts on auth:success). */
  setAccount(account: Partial<SettingsAccount>): void {
    this.account = { ...this.account, ...account };
    if (this.activeTab === 'account') this.renderPanel();
  }

  /** Push the live blocked list (from BlockService). Re-renders if visible. */
  setBlocked(blocked: BlockedPlayer[]): void {
    this.blocked = blocked;
    if (this.activeTab === 'account') this.renderPanel();
  }

  /** Called when the screen becomes visible — re-sync controls to store. */
  onShow(): void {
    this.renderPanel();
  }

  /** Open a specific tab (e.g. deep-link from the home avatar → Account). */
  openTab(tab: SettingsTab): void {
    this.activeTab = tab;
    this.tabBar.querySelectorAll<HTMLElement>('.settings-tab').forEach((el) => {
      el.classList.toggle('active', el.dataset.tab === tab);
    });
    this.renderPanel();
  }

  private createElement(): HTMLElement {
    const screen = document.createElement('div');
    screen.className = 'settings-screen';
    screen.innerHTML = `
      <div class="settings-topbar">
        <div class="settings-title">SETTINGS</div>
        <div class="settings-subtitle">TUNE YOUR GAME</div>
      </div>
      <div class="settings-tabs"></div>
      <div class="settings-panel"></div>
    `;

    this.tabBar = screen.querySelector('.settings-tabs')!;
    this.panel = screen.querySelector('.settings-panel')!;

    const tabs: Array<{ id: SettingsTab; label: string }> = [
      { id: 'account', label: 'Account' },
      { id: 'audio', label: 'Audio' },
      { id: 'graphics', label: 'Graphics' },
      { id: 'support', label: 'Support' },
    ];

    for (const { id, label } of tabs) {
      const btn = document.createElement('button');
      btn.className = 'settings-tab';
      btn.dataset.tab = id;
      btn.textContent = label;
      if (id === this.activeTab) btn.classList.add('active');
      btn.addEventListener('click', () => this.selectTab(id));
      this.tabBar.appendChild(btn);
    }

    return screen;
  }

  private selectTab(tab: SettingsTab): void {
    if (tab === this.activeTab) return;
    this.activeTab = tab;
    this.tabBar.querySelectorAll<HTMLElement>('.settings-tab').forEach((el) => {
      el.classList.toggle('active', el.dataset.tab === tab);
    });
    this.renderPanel();
  }

  /**
   * Re-query the live OS notification permission and refresh the toggle to
   * match. Called after a toggle action (from main.ts) and on every Account-tab
   * render. Only re-renders when the value actually changed, so there's no loop.
   */
  async refreshNotificationState(): Promise<void> {
    const granted = (await notificationService.getPermission()) === 'granted';
    if (granted !== this.notifGranted) {
      this.notifGranted = granted;
      if (this.activeTab === 'account') this.renderPanel();
    }
  }

  private renderPanel(): void {
    this.panel.innerHTML = '';
    switch (this.activeTab) {
      case 'audio': this.renderAudio(); break;
      case 'graphics': this.renderGraphics(); break;
      case 'account': this.renderAccount(); break;
      case 'support': this.renderSupport(); break;
    }
  }

  // ── Audio ──────────────────────────────────────────────────────────────

  private renderAudio(): void {
    // The Settings-tab path re-renders the whole panel on mute toggle (so the
    // tab's other groups stay correct); the detached path re-renders only
    // itself. Both build the same groups via renderAudioInto.
    this.renderAudioInto(this.panel, () => {
      if (this.activeTab === 'audio') this.renderPanel();
    });
  }

  /**
   * Build a standalone copy of the Audio panel (Sound + Voice groups) for
   * embedding OUTSIDE the Settings screen — e.g. the in-game menu's Sound view.
   * Wired to the same settingsStore, so it stays in sync with the Settings tab.
   *
   * The wrapper carries `.settings-screen` only to inherit that scope's CSS
   * variables; `.settings-audio-detached` neutralises the full-screen layout.
   * The returned element holds no SettingsScreen state — the caller owns it and
   * should drop it (remove from the DOM) when its host closes; a fresh one is
   * handed out on the next call.
   */
  buildAudioPanel(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'settings-screen settings-audio-detached';
    const render = (): void => this.renderAudioInto(wrap, render);
    render();
    return wrap;
  }

  /** Shared builder for both the Settings tab and the detached panel. */
  private renderAudioInto(target: HTMLElement, rerender: () => void): void {
    target.innerHTML = '';
    const a = settingsStore.get('audio');
    const group = this.group('Sound');

    group.appendChild(this.toggleRow('Mute all', a.muted, (v) => {
      settingsStore.update('audio', { muted: v });
      // Re-render so the disabled state on sliders reflects the mute.
      rerender();
    }));

    const disabled = a.muted;
    group.appendChild(this.sliderRow('Master', a.master, disabled, (v) => settingsStore.update('audio', { master: v })));
    group.appendChild(this.sliderRow('Music', a.music, disabled, (v) => settingsStore.update('audio', { music: v })));
    group.appendChild(this.sliderRow('Game FX', a.sfx, disabled, (v) => settingsStore.update('audio', { sfx: v })));
    group.appendChild(this.sliderRow('UI FX', a.ui, disabled, (v) => settingsStore.update('audio', { ui: v })));
    target.appendChild(group);

    const voice = this.group('Voice Chat');
    voice.appendChild(this.toggleRow('Enable mic', a.voiceChatEnabled, (v) => settingsStore.update('audio', { voiceChatEnabled: v })));
    voice.appendChild(this.sliderRow('Voice volume', a.voice, disabled, (v) => settingsStore.update('audio', { voice: v })));
    target.appendChild(voice);
  }

  // ── Graphics ───────────────────────────────────────────────────────────

  private renderGraphics(): void {
    const g = settingsStore.get('graphics');
    const group = this.group('Quality');

    const seg = document.createElement('div');
    seg.className = 'settings-segmented';
    seg.dataset.sound = 'button'; // LOW/MEDIUM/HIGH select buttons → button press
    (['low', 'medium', 'high'] as RenderQuality[]).forEach((q) => {
      const btn = document.createElement('button');
      btn.className = 'settings-seg-btn';
      btn.textContent = QUALITY_INFO[q].label;
      btn.classList.toggle('active', g.quality === q);
      btn.addEventListener('click', () => {
        settingsStore.update('graphics', { quality: q });
        if (this.activeTab === 'graphics') this.renderPanel();
      });
      seg.appendChild(btn);
    });
    group.appendChild(seg);

    const blurb = document.createElement('div');
    blurb.className = 'settings-blurb';
    blurb.textContent = QUALITY_INFO[g.quality].blurb;
    group.appendChild(blurb);
    this.panel.appendChild(group);

    const chat = settingsStore.get('chat');
    const display = this.group('Display');
    display.appendChild(this.toggleRow('Chat bubbles', chat.bubblesEnabled, (v) => settingsStore.update('chat', { bubblesEnabled: v })));
    const chatBlurb = document.createElement('div');
    chatBlurb.className = 'settings-blurb';
    chatBlurb.textContent = 'Show floating chat over players in the lobby and games.';
    display.appendChild(chatBlurb);
    this.panel.appendChild(display);
  }

  // ── Account ────────────────────────────────────────────────────────────

  private renderAccount(): void {
    const { username, email, picture, isGuest } = this.account;

    const card = document.createElement('div');
    card.className = 'settings-account-card';
    const avatar = picture
      ? `<img class="settings-avatar" src="${picture}" alt="">`
      : `<div class="settings-avatar settings-avatar-fallback">${this.initials(username)}</div>`;
    card.innerHTML = `
      ${avatar}
      <div class="settings-account-meta">
        <div class="settings-account-name">${this.escape(username)}</div>
        <div class="settings-account-email">${isGuest ? 'Guest account' : this.escape(email || 'No email set')}</div>
      </div>
    `;
    const photoBtn = document.createElement('button');
    photoBtn.className = 'settings-btn settings-btn-cyan';
    photoBtn.textContent = 'Change Photo';
    photoBtn.addEventListener('click', () => this.emit('account:change-picture', undefined));
    card.appendChild(photoBtn);
    this.panel.appendChild(card);

    // Order under the profile card: Account → Push Notifications → Privacy →
    // Log out.
    if (isGuest) {
      // Guests get the expandable "connect & bag 100M" section — the bonus is
      // the hook that drives sign-ups, so advertise it on the row itself.
      this.panel.appendChild(this.renderConnect());
    } else {
      const group = this.group('Account');
      group.appendChild(this.actionRow('Update email', email || 'Not set', 'cyan', () => this.emit('account:update-email', undefined)));
      group.appendChild(this.actionRow('Recover account', 'Reset password', 'cyan', () => this.emit('account:recover', undefined)));
      this.panel.appendChild(group);
    }

    // Notifications — account-level preference. Shown ON only when the OS
    // permission is actually granted AND the player hasn't muted it, so the
    // switch never falsely reads ON before the player has accepted.
    const notif = settingsStore.get('notifications');
    const notifGroup = this.group('Notifications');
    notifGroup.appendChild(this.toggleRow('Push notifications', this.notifGranted && notif.enabled, (v) => {
      settingsStore.update('notifications', { enabled: v });
      // main.ts drives the OS prompt (on) / token unregister (off), then calls
      // refreshNotificationState() so this toggle re-reads the real permission.
      this.emit('notifications:toggle', { enabled: v });
    }));
    const notifBlurb = document.createElement('div');
    notifBlurb.className = 'settings-blurb';
    notifBlurb.textContent = 'Get pinged when your crew messages you and when your free Stacks are ready.';
    notifGroup.appendChild(notifBlurb);
    this.panel.appendChild(notifGroup);
    // Reconcile the toggle with the live OS permission (async; re-renders only
    // if it differs from the cached value, so no render loop).
    void this.refreshNotificationState();

    // Privacy — policy link + blocked players accordion.
    this.panel.appendChild(this.renderBlocked());

    const danger = this.group('');
    danger.appendChild(this.actionRow('Log out', '', 'danger', () => this.emit('account:logout', undefined)));
    this.panel.appendChild(danger);
  }

  /**
   * Guest-only "Connect & bag 100M" section. The header row advertises the
   * bonus; tapping it expands in place (like the Blocked Players accordion) to
   * reveal the Apple/Google buttons. Each provider emits `account:connect`,
   * which main.ts turns into the social sign-in + guest-link + 100M claim.
   */
  private renderConnect(): HTMLElement {
    const group = this.group('Account');

    const header = document.createElement('button');
    header.className = 'settings-row settings-action-row accent-gold';
    header.innerHTML = `
      <div class="settings-action-text">
        <span class="settings-row-label">Connect &amp; bag 100M</span>
        <span class="settings-row-hint">Save your progress &amp; claim ${CONNECT_BONUS_DISPLAY}</span>
      </div>
      <span class="settings-chevron">${this.connectExpanded ? '⌄' : '›'}</span>
    `;
    header.addEventListener('click', () => {
      this.connectExpanded = !this.connectExpanded;
      if (this.activeTab === 'account') this.renderPanel();
    });
    group.appendChild(header);

    if (!this.connectExpanded) return group;

    const body = document.createElement('div');
    body.className = 'settings-connect-body';

    const hook = document.createElement('div');
    hook.className = 'settings-connect-hook';
    hook.innerHTML = `
      <span class="settings-connect-coin">💰</span>
      <span>Link an account to claim <b>${CONNECT_BONUS_DISPLAY}</b> Stacks — and never lose your dice or progress.</span>
    `;
    body.appendChild(hook);

    const apple = document.createElement('button');
    apple.className = 'login-btn-social login-btn-apple';
    apple.innerHTML = `${APPLE_GLYPH} Sign in with Apple`;
    apple.addEventListener('click', () => this.emit('account:connect', { provider: 'apple' }));
    body.appendChild(apple);

    const google = document.createElement('button');
    google.className = 'login-btn-social login-btn-google';
    google.innerHTML = `${GOOGLE_GLYPH} Sign in with Google`;
    google.addEventListener('click', () => this.emit('account:connect', { provider: 'google' }));
    body.appendChild(google);

    group.appendChild(body);
    return group;
  }

  /** Collapsible "Blocked Players" manager. Tap the header to expand the list. */
  private renderBlocked(): HTMLElement {
    const group = this.group('Privacy');

    // Privacy Policy link (opens in the system browser). Required for store
    // submission and the natural top of the Privacy group.
    const policy = document.createElement('a');
    policy.className = 'settings-row settings-action-row accent-cyan';
    policy.href = `${LEGAL_BASE}/privacy`;
    policy.target = '_blank';
    policy.rel = 'noopener';
    // Strip the default anchor styling (blue text + underline) — this row should
    // read like the other action rows, not a hyperlink.
    policy.style.textDecoration = 'none';
    policy.style.color = 'inherit';
    policy.innerHTML = `
      <div class="settings-action-text">
        <span class="settings-row-label">Privacy Policy</span>
        <span class="settings-row-hint">How we handle your data</span>
      </div>
      <span class="settings-chevron">›</span>
    `;
    group.appendChild(policy);

    // Terms of Service link (same treatment as the policy link).
    const terms = document.createElement('a');
    terms.className = 'settings-row settings-action-row accent-cyan';
    terms.href = `${LEGAL_BASE}/terms`;
    terms.target = '_blank';
    terms.rel = 'noopener';
    terms.style.textDecoration = 'none';
    terms.style.color = 'inherit';
    terms.innerHTML = `
      <div class="settings-action-text">
        <span class="settings-row-label">Terms of Service</span>
        <span class="settings-row-hint">The rules of play</span>
      </div>
      <span class="settings-chevron">›</span>
    `;
    group.appendChild(terms);

    const header = document.createElement('button');
    header.className = 'settings-row settings-action-row accent-cyan';
    header.innerHTML = `
      <div class="settings-action-text">
        <span class="settings-row-label">Blocked Players</span>
        <span class="settings-row-hint">${this.blocked.length} blocked</span>
      </div>
      <span class="settings-chevron">${this.blockedExpanded ? '⌄' : '›'}</span>
    `;
    header.addEventListener('click', () => {
      this.blockedExpanded = !this.blockedExpanded;
      if (this.activeTab === 'account') this.renderPanel();
    });
    group.appendChild(header);

    if (!this.blockedExpanded) return group;

    const list = document.createElement('div');
    list.className = 'settings-blocked-list';

    if (this.blocked.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'settings-blocked-empty';
      empty.textContent = "You haven't blocked anyone.";
      list.appendChild(empty);
    } else {
      for (const b of this.blocked) {
        const row = document.createElement('div');
        row.className = 'settings-blocked-row';

        const avatar = b.picture
          ? `<img class="settings-blocked-avatar" src="${b.picture}" alt="">`
          : `<div class="settings-blocked-avatar">${this.initials(b.username)}</div>`;

        const unblock = document.createElement('button');
        unblock.className = 'settings-blocked-unblock';
        unblock.textContent = 'UNBLOCK';
        unblock.addEventListener('click', () => this.emit('account:unblock', { playerId: b.id }));

        row.innerHTML = `${avatar}<span class="settings-blocked-name">${this.escape(b.username)}</span>`;
        row.appendChild(unblock);
        list.appendChild(row);
      }
    }
    group.appendChild(list);
    return group;
  }

  // ── Support ────────────────────────────────────────────────────────────

  private renderSupport(): void {
    const ticket = this.group('Get Help');
    const subject = document.createElement('input');
    subject.className = 'settings-input';
    subject.type = 'text';
    subject.maxLength = 80;
    subject.placeholder = 'Subject';
    const body = document.createElement('textarea');
    body.className = 'settings-textarea';
    body.maxLength = 1000;
    body.placeholder = 'Describe your issue…';
    const send = document.createElement('button');
    send.className = 'settings-btn settings-btn-gold';
    send.textContent = 'Open Ticket';
    send.addEventListener('click', () => {
      const s = subject.value.trim();
      const m = body.value.trim();
      if (m.length < 5) { this.flash(send, 'Add details'); return; }
      this.emit('support:open-ticket', { subject: s || '(no subject)', message: m });
      subject.value = '';
      body.value = '';
      this.flash(send, 'Sent ✓');
    });
    ticket.appendChild(subject);
    ticket.appendChild(body);
    ticket.appendChild(send);
    this.panel.appendChild(ticket);

    const fb = this.group('Feedback');
    const fbBody = document.createElement('textarea');
    fbBody.className = 'settings-textarea';
    fbBody.maxLength = 1000;
    fbBody.placeholder = 'Tell us what you think…';
    const fbSend = document.createElement('button');
    fbSend.className = 'settings-btn settings-btn-cyan';
    fbSend.textContent = 'Send Feedback';
    fbSend.addEventListener('click', () => {
      const m = fbBody.value.trim();
      if (m.length < 5) { this.flash(fbSend, 'Say more'); return; }
      this.emit('support:feedback', { message: m });
      fbBody.value = '';
      this.flash(fbSend, 'Thanks ✓');
    });
    fb.appendChild(fbBody);
    fb.appendChild(fbSend);
    this.panel.appendChild(fb);

    const rate = this.group('Enjoying the game?');
    const rateBtn = document.createElement('button');
    rateBtn.className = 'settings-btn settings-btn-cyan';
    rateBtn.textContent = 'Leave a Rating';
    rateBtn.addEventListener('click', () => {
      this.emit('support:rate', undefined);
      this.flash(rateBtn, 'Thanks ✓');
    });
    rate.appendChild(rateBtn);
    this.panel.appendChild(rate);

    const about = document.createElement('div');
    about.className = 'settings-about';
    about.innerHTML = `
      <div class="settings-about-version">Street Dice Legends · v${this.escape(APP_VERSION)}</div>
      <div class="settings-about-links">
        <a href="${LEGAL_BASE}/privacy" target="_blank" rel="noopener">Privacy</a>
        <span>·</span>
        <a href="${LEGAL_BASE}/terms" target="_blank" rel="noopener">Terms</a>
      </div>
    `;
    this.panel.appendChild(about);
  }

  // ── Reusable control builders ────────────────────────────────────────────

  private group(title: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'settings-group';
    if (title) {
      const h = document.createElement('div');
      h.className = 'settings-group-title';
      h.textContent = title;
      el.appendChild(h);
    }
    return el;
  }

  private sliderRow(label: string, value: number, disabled: boolean, onChange: (v: number) => void): HTMLElement {
    const row = document.createElement('div');
    row.className = 'settings-row settings-slider-row';
    if (disabled) row.classList.add('disabled');

    const name = document.createElement('span');
    name.className = 'settings-row-label';
    name.textContent = label;

    const pct = document.createElement('span');
    pct.className = 'settings-slider-val';
    pct.textContent = `${Math.round(value * 100)}%`;

    const range = document.createElement('input');
    range.className = 'settings-range';
    range.type = 'range';
    range.min = '0';
    range.max = '100';
    range.value = String(Math.round(value * 100));
    range.disabled = disabled;
    range.addEventListener('input', () => {
      const v = Number(range.value) / 100;
      pct.textContent = `${range.value}%`;
      onChange(v);
    });

    const head = document.createElement('div');
    head.className = 'settings-slider-head';
    head.appendChild(name);
    head.appendChild(pct);

    row.appendChild(head);
    row.appendChild(range);
    return row;
  }

  private toggleRow(label: string, value: boolean, onChange: (v: boolean) => void): HTMLElement {
    const row = document.createElement('div');
    row.className = 'settings-row';

    const name = document.createElement('span');
    name.className = 'settings-row-label';
    name.textContent = label;

    const sw = document.createElement('button');
    sw.className = 'settings-switch';
    sw.classList.toggle('on', value);
    sw.setAttribute('role', 'switch');
    sw.setAttribute('aria-checked', String(value));
    sw.innerHTML = '<span class="settings-switch-knob"></span>';
    sw.addEventListener('click', () => {
      const next = !sw.classList.contains('on');
      sw.classList.toggle('on', next);
      sw.setAttribute('aria-checked', String(next));
      onChange(next);
    });

    row.appendChild(name);
    row.appendChild(sw);
    return row;
  }

  private actionRow(label: string, hint: string, accent: 'cyan' | 'gold' | 'danger', onClick: () => void): HTMLElement {
    const row = document.createElement('button');
    row.className = `settings-row settings-action-row accent-${accent}`;

    const left = document.createElement('div');
    left.className = 'settings-action-text';
    left.innerHTML = `<span class="settings-row-label">${this.escape(label)}</span>${hint ? `<span class="settings-row-hint">${this.escape(hint)}</span>` : ''}`;

    const chevron = document.createElement('span');
    chevron.className = 'settings-chevron';
    chevron.textContent = '›';

    row.appendChild(left);
    row.appendChild(chevron);
    row.addEventListener('click', onClick);
    return row;
  }

  private flash(btn: HTMLButtonElement, text: string): void {
    const original = btn.textContent;
    btn.textContent = text;
    btn.classList.add('flashing');
    setTimeout(() => {
      btn.textContent = original;
      btn.classList.remove('flashing');
    }, 1400);
  }

  private initials(name: string): string {
    return name.trim().slice(0, 2).toUpperCase() || '?';
  }

  private escape(s: string): string {
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }
}
